import { Icon } from './Icon'
import { STATUS, AGG_STATUS } from '../domain/catalog'
import { journeyDisplayStatus } from '../domain/engine'
import type { JourneyStatus, Ticket, TicketStatus } from '../domain/types'
import { env } from '../config/env'

const STATUS_ICON: Record<string, string> = {
  clock: 'clock', bell: 'megaphone', play: 'play', check: 'check', x: 'x', rotate: 'rotate', slash: 'slash', pin: 'pin',
}

export function StatusBadge({ status, blocked, overdue, sm }: { status: TicketStatus; blocked?: boolean; overdue?: boolean; sm?: boolean }) {
  if (overdue) {
    return (
      <span className={'fz-status fz-status--danger ' + (sm ? 'fz-status--sm' : '')}>
        <Icon name="alert" size={sm ? 11 : 13} /> Atrasado
      </span>
    )
  }
  if (blocked) {
    return (
      <span className={'fz-status fz-status--locked ' + (sm ? 'fz-status--sm' : '')}>
        <Icon name="lock" size={sm ? 11 : 13} /> Bloqueado
      </span>
    )
  }
  const cfg = STATUS[status] || STATUS.Opened
  return (
    <span className={`fz-status fz-status--${cfg.tone} ` + (sm ? 'fz-status--sm' : '')}>
      <Icon name={STATUS_ICON[cfg.icon] || 'clock'} size={sm ? 11 : 13} /> {cfg.label}
    </span>
  )
}

export function StatusDot({ tone }: { tone: string }) {
  return <span className={`fz-dot fz-dot--${tone}`} />
}

export function AggStatusBadge({ tickets, journeyStatus, sm }: { tickets: Ticket[]; journeyStatus?: JourneyStatus; sm?: boolean }) {
  const hasOverdue = !env.singleDay && tickets.some((t) => t.isOverdue || t.scheduleState === 'Expired')
  if (hasOverdue) {
    return (
      <span className={'fz-status fz-status--danger ' + (sm ? 'fz-status--sm' : '')}>
        <Icon name="alert" size={sm ? 11 : 13} /> Atrasado
      </span>
    )
  }
  const st = journeyDisplayStatus(journeyStatus, tickets)
  const cfg = AGG_STATUS[st]
  const iconMap: Record<string, string> = {
    Aguardando: 'clock', Agendada: 'clock', Confirmada: 'check', AguardandoChamada: 'clock',
    Chamado: 'megaphone', EmAtendimento: 'play', Completo: 'check', ParcialmenteCompleto: 'alert', Cancelado: 'x',
  }
  return (
    <span className={`fz-status fz-status--${cfg.tone} ` + (sm ? 'fz-status--sm' : '')}>
      <Icon name={iconMap[st]} size={sm ? 11 : 13} /> {cfg.label}
    </span>
  )
}

// Flag de prioridade — glifos vindos do filazero-app (accessibility-white.svg / elderly.svg)
export function PriorityFlag({ prioridade }: { prioridade: number }) {
  if (!prioridade) {return null}
  const high = prioridade === 2
  const label = high ? 'Preferencial +80' : 'Atendimento preferencial'
  return (
    <span className={'fz-flag ' + (high ? 'is-high' : '')} role="img" title={label} aria-label={label}>
      {high ? (
        <svg viewBox="0 0 54.891 54.891" width="11" height="11" fill="currentColor" aria-hidden="true">
        <path d="M43.154,22.18c0.708,0.449-0.322-0.485-0.517-0.742c-0.688-0.907-1.332-1.843-1.979-2.777 c-0.763-1.103-1.513-2.216-2.25-3.336c-0.953-1.447-2.896-1.627-4.446-2.019c-2.146-0.542-4.306-1.022-6.494-1.344 c-3.826-1.362-8.872,2.807-6.229,7.326c0.097,0.166,0.191,0.334,0.287,0.5c-2.013,2.567-5.042,3.522-8.818,3.127 c-2.318-0.243-2.87,2.424-1.697,3.889v27.648h1.697v-26.74c4.584,0.479,8.441-0.841,11.271-3.586 c1.152,2.093,2.275,4.201,3.369,6.325c-2.994,2.064-5.609,4.564-7.9,7.443c-0.787,0.988-0.784,2.014-0.346,2.84 c-0.017,0.291,0.011,0.605,0.096,0.941c0.882,3.487,1.763,6.975,2.645,10.463c0.937,3.719,6.819,2.658,5.875-1.08 c-0.882-3.488-1.763-6.977-2.645-10.465c-0.01-0.041-0.023-0.078-0.035-0.119c1.878-2.205,4.059-4.15,6.52-5.684 c0.297,5.813,0.594,11.985,0.893,17.364c0.214,3.845,6.182,3.545,5.967-0.312c-0.408-7.403-0.818-16.301-1.229-23.703 c-0.037-0.675-0.254-1.219-0.58-1.641c-0.098-0.328-0.228-0.66-0.398-0.995c-1.162-2.263-2.359-4.51-3.592-6.739 c0.518,0.138,1.033,0.279,1.547,0.42c1.688,2.739,3.557,6.238,6.135,7.875C43.39,29.023,46.224,24.128,43.154,22.18z" />
        <path d="M24.226,3.156c-0.771-2.43-3.5-3.733-6.093-2.909C15.54,1.072,14.064,3.71,14.836,6.14 L24.226,3.156z" />
        <path d="M12.676,9.289L27.809,4.48c0.226-0.072,0.375-0.241,0.333-0.378L24.701,3.6l-9.788,3.111 l-2.727,2.461C12.23,9.31,12.449,9.361,12.676,9.289z" />
        <path d="M22.006,12.042c2.552-0.811,3.961-3.538,3.149-6.089l-9.237,2.942 C16.729,11.445,19.457,12.856,22.006,12.042z" />
        </svg>
      ) : (
        <svg viewBox="776 270.9 300 300" width="12" height="12" fill="currentColor" aria-hidden="true">
        <path d="M1036.7 407.1c-3.6-3.6-8.5-5.6-13.6-5.3l-61.4 3.4 33.8-38.5c4.8-5.5 6.2-12.8 4.3-19.3-1-4.5-3.7-8.5-7.9-11.1-.1-.1-80.7-46.9-80.7-46.9-6.6-3.8-14.9-3-20.6 2.1l-39.4 35.1c-7.3 6.5-7.9 17.6-1.4 24.8 6.5 7.3 17.6 7.9 24.8 1.4l29.8-26.6 24.7 14.3-43.6 49.6c-18.1 2.9-34.3 11.3-47.1 23.3l22.7 22.7C871.4 426.8 885 421 900 421c31.9 0 57.9 26 57.9 57.9 0 15-5.7 28.6-15.1 38.9l22.7 22.7c15.2-16.1 24.4-37.8 24.4-61.6 0-14.2-3.3-27.7-9.2-39.6l23.8-1.3-5.8 70.9c-.8 9.7 6.4 18.2 16.1 19 .5 0 1 .1 1.4.1 9.1 0 16.8-7 17.5-16.2l7.4-91c.9-5.1-.9-10.1-4.4-13.7zM1004.1 331.7c16.3 0 29.5-13.2 29.5-29.5s-13.2-29.5-29.5-29.5-29.5 13.2-29.5 29.5 13.2 29.5 29.5 29.5zM900.3 536.9c-31.9 0-57.9-26-57.9-57.9 0-12 3.7-23.2 10-32.5l-23-23c-12 15.3-19.1 34.5-19.1 55.5 0 49.7 40.3 90 90 90 20.9 0 40.2-7.2 55.5-19.1l-23-23c-9.3 6.3-20.5 10-32.5 10z" />
        </svg>
      )}
    </span>
  )
}
